import FilterPanel from '@components/FilterPanel/FilterPanel';
import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { Form, Input, DatePicker, Button, Radio, Select } from 'antd';
import { useStore } from 'effector-react';
import posts, { getList, setFilters } from '@stores/posts';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import useTranslate from '@hooks/useTranslate';
import componentContent from './PostFilter.content.json';
import css from './PostsFilters.module.less';
import formatFilters from '@helpers/formatFilters';
import moment, { Moment } from 'moment';
import history from '@helpers/history';
import GameSelector from '../GameSelector';
import localeWeightStore, { localeWeightMap, setLocaleWeight, LOCALE } from '@stores/localeWeight';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface PostsFiltersProps {}

const PostsFilters: FunctionComponent<PostsFiltersProps> = () => {
  const { filters, preloaders } = useStore(posts);
  const [form] = Form.useForm();
  const localeName = useStore(localeWeightStore);

  const initialValues = useMemo(() => {
    return {
      ...filters,
      locale: localeName,
      date: [
        filters.createdFrom ? moment(filters.createdFrom) : undefined,
        filters.createdTo ? moment(filters.createdTo) : undefined,
      ],
    };
  }, [filters, localeName]);

  useEffect(() => {
    form.resetFields();
  }, [initialValues, form]);

  const content = useTranslate(componentContent);
  const onFinish = (filters: Record<string, any>) => {
    getList({ filters: formatFilters(filters) });
  };
  const clearFilters = () => {
    setLocaleWeight(LOCALE.ALL);
    history.replace({ ...history.location, search: '' });
    getList({ filters: {} });
    form.resetFields();
  };

  const disabledDate = (date: Moment) => {
    return date.diff(moment()) >= 0;
  };

  const updateFormHandler = useCallback(
    (value) => {
      const change = value?.[0];
      if (change?.name?.[0] === 'gameId') {
        if (!change?.value) {
          setFilters({
            ...filters,
            visibleBlockPost: 'ALL',
          });
        }
      }
    },
    [filters],
  );

  const onChangeLocale = useCallback((newLocale: LOCALE) => {
    setLocaleWeight(newLocale);
  }, []);

  const onChangeVisible = useCallback((value: string) => {
    setFilters({
      ...filters,
      visibleBlockPost: value,
    });
  }, []);

  return (
    <Form
      layout='vertical'
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      onFieldsChange={updateFormHandler}
    >
      <FilterPanel
        actions={
          <div className={css.formFooter}>
            <Button
              htmlType='submit'
              icon={<SearchOutlined />}
              type='primary'
              loading={!!preloaders.getList}
            >
              {content.search}
            </Button>
            <Button
              htmlType='button'
              icon={<ClearOutlined />}
              onClick={clearFilters}
              loading={!!preloaders.getList}
            >
              {content.clear}
            </Button>
          </div>
        }
      >
        <Form.Item
          label={content.mobileUrlPost}
          name='mobileUrlPost'
        >
          <Input placeholder={content.mobileUrlPostPlaceholder} />
        </Form.Item>
        <Form.Item
          label={content.nickname}
          name='author'
        >
          <Input placeholder={content.nicknamePlaceholder} />
        </Form.Item>
        <Form.Item
          label={content.date}
          name='date'
        >
          <RangePicker
            showTime={{
              defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('00:00:00', 'HH:mm:ss')],
            }}
            format='DD-MM-YYYY HH:mm:ss'
            disabledDate={disabledDate}
            allowEmpty={[true, true]}
            style={{ width: '100%' }}
            placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
          />
        </Form.Item>
        <Form.Item
          label={content.game}
          name='gameId'
        >
          <GameSelector />
        </Form.Item>
        <Form.Item
          label={content.hashtag}
          name='hashtag'
        >
          <Input placeholder={content.hashtagPlaceholder} />
        </Form.Item>
        <Form.Item
          label={content.visibleBlockPost}
          name='visibleBlockPost'
        >
          <Radio.Group
            buttonStyle='solid'
            onChange={(e) => onChangeVisible(e.target.value)}
          >
            <Radio.Button value='ALL'>{content.all}</Radio.Button>
            <Radio.Button value='UNBLOCK'>{content.ublock}</Radio.Button>
            <Radio.Button value='BLOCK'>{content.block}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={content.postTypes}
          name='postTypes'
        >
          <Select
            defaultActiveFirstOption={false}
            mode='multiple'
          >
            <Option value='IMAGE'>{content.image}</Option>
            <Option value='VIDEO'>{content.video}</Option>
            <Option value='HAS_PLAYER_ID'>{content.playerID}</Option>
          </Select>
        </Form.Item>
        <Form.Item
          label={content.locale}
          name='locale'
        >
          <Select
            style={{ width: 120 }}
            onChange={onChangeLocale}
          >
            {localeWeightMap &&
              Object.entries(localeWeightMap).map(([key, value]) => {
                return (
                  <Option
                    value={key}
                    key={key}
                  >
                    {value.name}
                  </Option>
                );
              })}
          </Select>
        </Form.Item>
      </FilterPanel>
    </Form>
  );
};

export default PostsFilters;
